/**
 * Default Candidate Templates
 *
 * Sample candidates used during onboarding so users can experience the scoring
 * flow without needing to upload their own resumes.
 */
export const DEFAULT_CANDIDATE_TEMPLATES = [
    {
        id: 'sample-john-doe',
        label: 'John Doe',
        icon: '👨‍💼',
        tagline: 'Warehouse associate · 3 yr',
        data: {
            name: 'John Doe',
            location: 'Austin, TX',
            timezone: 'America/Chicago',
            phone: '+1 512-555-0142',
            email: 'john.doe@example.com',
            experience: [
                {
                    startDate: { year: 2022, month: 3 },
                    endDate: { year: 2025, month: 12 },
                    position: 'Warehouse Associate',
                    companyName: 'FastShip Logistics',
                    duties: 'Picked, packed, and shipped 120+ orders per shift. Operated pallet jacks and handheld scanners. Maintained a 99.5% accuracy rate and zero safety incidents.',
                },
                {
                    startDate: { year: 2020, month: 6 },
                    endDate: { year: 2022, month: 2 },
                    position: 'Retail Sales Associate',
                    companyName: 'BrightMart',
                    duties: 'Assisted customers on the floor, handled POS transactions, managed inventory restocking. Promoted to shift lead after 8 months.',
                },
            ],
            strengths: 'Reliable, punctual, and physically fit. Quick to learn warehouse management systems. Comfortable working overtime and weekend shifts.',
            weaknesses: 'Limited experience operating sit-down forklifts; currently working toward certification.',
            fiveYears: 'Grow into an operations supervisor role, managing a team of warehouse associates and driving process improvements.',
        },
        previewByJobTemplateId: {
            // Logistics — his home turf
            'logistics-warehouse-associate': {
                overallScore: 9.2,
                matches: [
                    '3+ years as a Warehouse Associate at FastShip Logistics',
                    '120+ orders/shift with 99.5% pick accuracy',
                    'Hands-on with pallet jacks and handheld WMS scanners',
                    'Zero safety incidents · reliable attendance',
                ],
                concerns: [],
            },
            'logistics-forklift-operator': {
                overallScore: 7.1,
                matches: [
                    'Warehouse-floor experience with pallet jacks',
                    'Familiar with WMS scanners and pick paths',
                    'Comfortable in fast-paced shift environments',
                ],
                concerns: [
                    'Sit-down forklift certification still in progress',
                    'No documented hours operating a stand-up reach truck',
                ],
            },
            'logistics-shipping-receiving': {
                overallScore: 8.8,
                matches: [
                    '3+ years picking, packing, and shipping daily',
                    'Maintained 99.5% order accuracy under quota',
                    'Comfortable scanning, labeling, and load-out workflows',
                ],
                concerns: [
                    'Receiving experience is implied but not explicit on resume',
                ],
            },
            'logistics-inventory-specialist': {
                overallScore: 8.2,
                matches: [
                    'Hands-on warehouse + retail restocking experience',
                    'Strong attention to detail (99.5% accuracy)',
                    'Comfortable with handheld scanners and WMS',
                ],
                concerns: [
                    'No formal cycle-count or inventory-control title on record',
                ],
            },
            'logistics-cdl-driver': {
                overallScore: 3.8,
                matches: [
                    'Reliable, punctual, and physically fit',
                ],
                concerns: [
                    'No CDL listed on resume',
                    'No commercial driving history',
                    'Strong fit for warehouse roles instead',
                ],
            },
            // Retail — transferable BrightMart experience
            'retail-sales-associate': {
                overallScore: 8.4,
                matches: [
                    'Prior Retail Sales Associate at BrightMart',
                    'Promoted to shift lead within 8 months',
                    'Comfortable on POS and customer-facing floor work',
                ],
                concerns: [
                    'Currently in warehouse role — may want to confirm interest in returning to retail',
                ],
            },
            'retail-cashier': {
                overallScore: 8.0,
                matches: [
                    'Hands-on POS experience at BrightMart',
                    'Customer-facing floor experience',
                    'Reliable and punctual per prior role',
                ],
                concerns: [
                    'Has since moved into warehouse work — may prefer that environment',
                ],
            },
            'retail-stock-associate': {
                overallScore: 8.9,
                matches: [
                    'Warehouse picking/packing + retail restocking background',
                    'Comfortable with inventory scanners',
                    'Used to overtime and weekend shifts',
                ],
                concerns: [],
            },
            'retail-store-manager': {
                overallScore: 5.2,
                matches: [
                    'Promoted to shift lead at BrightMart',
                    'Trusted with floor responsibilities',
                ],
                concerns: [
                    'No full store-manager title on resume',
                    'No P&L or hiring experience documented',
                ],
            },
            // Delivery — physical/warehouse transferable
            'delivery-package-handler': {
                overallScore: 8.6,
                matches: [
                    '3+ years moving and scanning packages',
                    'Comfortable lifting 50+ lbs all shift',
                    'Reliable attendance · zero safety incidents',
                ],
                concerns: [],
            },
            'delivery-driver': {
                overallScore: 5.4,
                matches: [
                    'Reliable and punctual',
                    'Comfortable with handheld scanners and routing apps (warehouse equivalent)',
                ],
                concerns: [
                    'No commercial driving record on resume',
                    'Strong fit for warehouse/handler roles instead',
                ],
            },
            // Hospitality — honest mismatch
            'hospitality-server': {
                overallScore: 4.1,
                matches: [
                    'Customer-facing experience from BrightMart retail floor',
                    'Reliable and works well in fast-paced environments',
                ],
                concerns: [
                    'No restaurant or serving experience on resume',
                    'No food-handler certification listed',
                    'Stronger candidates likely available for this role',
                ],
            },
            'hospitality-line-cook': {
                overallScore: 3.2,
                matches: [
                    'Comfortable with repetitive physical work and quotas',
                ],
                concerns: [
                    'No kitchen or food-prep experience documented',
                    'No food-safety certification',
                ],
            },
        },
        defaultPreview: {
            overallScore: 6.6,
            matches: [
                '3+ years hands-on warehouse experience',
                'Promoted to shift lead within 8 months at BrightMart',
                'Reliable, punctual, zero safety incidents',
                'Comfortable with overtime and weekend shifts',
            ],
            concerns: [
                'Background is warehouse + retail — adjacent skills only for this role',
                'Worth confirming interest in this role type during screening',
            ],
        },
    },
    {
        id: 'sample-maria-santos',
        label: 'Maria Santos',
        icon: '👷',
        tagline: 'Forklift operator · CDL · 6 yr',
        data: {
            name: 'Maria Santos',
            location: 'Phoenix, AZ',
            timezone: 'America/Phoenix',
            phone: '+1 602-555-0187',
            email: 'maria.santos@example.com',
            experience: [
                {
                    startDate: { year: 2019, month: 8 },
                    endDate: { year: 2025, month: 11 },
                    position: 'Forklift Operator',
                    companyName: 'PrimeFreight Distribution',
                    duties: 'Operated sit-down and stand-up forklifts across a 400k sq ft facility. Loaded/unloaded 30+ trailers per shift. Trained 6 junior operators. Zero safety incidents in 6 years. OSHA-certified.',
                },
                {
                    startDate: { year: 2017, month: 5 },
                    endDate: { year: 2019, month: 7 },
                    position: 'Delivery Driver',
                    companyName: 'Sunbelt Logistics',
                    duties: 'Ran 40+ residential and commercial routes weekly with a 26ft box truck. Held Class A CDL. Maintained a 98% on-time rate.',
                },
            ],
            strengths: 'Six years of certified forklift experience. Class A CDL with clean driving record. Trusted trainer for new hires. Strong safety mindset.',
            weaknesses: 'Prefers dayside shifts; limited overnight experience.',
            fiveYears: 'Move into a warehouse operations lead or safety supervisor role.',
        },
        previewByJobTemplateId: {
            'logistics-warehouse-associate': {
                overallScore: 8.5,
                matches: [
                    '6 years on the warehouse floor at PrimeFreight',
                    'OSHA-certified · zero safety incidents',
                    'Loaded/unloaded 30+ trailers per shift',
                ],
                concerns: [
                    'Prefers dayside shifts — confirm if nights are required',
                ],
            },
            'logistics-forklift-operator': {
                overallScore: 9.4,
                matches: [
                    '6 years on sit-down and stand-up forklifts',
                    'OSHA-certified · trained 6 junior operators',
                    'Zero safety incidents across full tenure',
                    'Handles 30+ trailer loads per shift',
                ],
                concerns: [],
            },
            'logistics-cdl-driver': {
                overallScore: 8.6,
                matches: [
                    'Active Class A CDL',
                    'Prior delivery-driver experience (2 yr)',
                    '98% on-time rate · clean driving record',
                ],
                concerns: [
                    'Has been off the road for 6 years — brief refresher may be needed',
                ],
            },
            'delivery-driver': {
                overallScore: 8.8,
                matches: [
                    'Class A CDL with clean record',
                    '2 years running 40+ delivery routes/week',
                    '98% on-time delivery rate',
                ],
                concerns: [
                    'Recent work is warehouse-based, not on the road',
                ],
            },
            'delivery-package-handler': {
                overallScore: 8.7,
                matches: [
                    'Physically fit · handles 30+ trailer loads/shift',
                    'OSHA-certified · zero safety incidents',
                ],
                concerns: [],
            },
            'retail-stock-associate': {
                overallScore: 7.2,
                matches: [
                    'Strong warehouse handling background',
                    'Comfortable with heavy lifting and shift work',
                ],
                concerns: [
                    'No direct retail floor experience',
                ],
            },
        },
        defaultPreview: {
            overallScore: 6.4,
            matches: [
                '6 years of physical, quota-driven work',
                'OSHA-certified · zero safety incidents',
                'Trusted trainer for new hires',
            ],
            concerns: [
                'Background is warehouse/driving — transferable skills only for this role',
                'May need training on role-specific systems and workflows',
            ],
        },
    },
    {
        id: 'sample-priya-rao',
        label: 'Priya Rao',
        icon: '👩‍💼',
        tagline: 'Retail sales lead · 5 yr',
        data: {
            name: 'Priya Rao',
            location: 'Seattle, WA',
            timezone: 'America/Los_Angeles',
            phone: '+1 206-555-0119',
            email: 'priya.rao@example.com',
            experience: [
                {
                    startDate: { year: 2022, month: 4 },
                    endDate: { year: 2025, month: 12 },
                    position: 'Sales Lead',
                    companyName: 'Northline Apparel',
                    duties: 'Led a 6-person retail team on the floor. Hit sales quota 11 of 12 quarters. Handled POS, opening/closing, inventory receiving, and customer escalations. Trained new hires on sales technique.',
                },
                {
                    startDate: { year: 2020, month: 1 },
                    endDate: { year: 2022, month: 3 },
                    position: 'Front Desk Associate',
                    companyName: 'Cascade Boutique Hotel',
                    duties: 'Managed guest check-in/out, reservations, and concierge requests. Handled cash and card payments. Praised for guest-recovery skills in 40+ TripAdvisor reviews.',
                },
            ],
            strengths: 'Warm, articulate communicator. Comfortable de-escalating upset customers. Trusted with cash and shift-lead responsibilities.',
            weaknesses: 'No experience with warehouse equipment or heavy lifting.',
            fiveYears: 'Grow into a store manager or district trainer role.',
        },
        previewByJobTemplateId: {
            'retail-sales-associate': {
                overallScore: 8.9,
                matches: [
                    '3+ years as Sales Lead at Northline Apparel',
                    'Hit sales quota 11 of 12 quarters',
                    'Trained new hires on sales technique',
                    'Praised for guest-recovery in 40+ hotel reviews',
                ],
                concerns: [],
            },
            'retail-cashier': {
                overallScore: 8.5,
                matches: [
                    'Hands-on POS and cash-handling across two roles',
                    'Comfortable de-escalating upset customers',
                ],
                concerns: [
                    'Currently in a lead role — may be over-qualified for pure cashier',
                ],
            },
            'retail-store-manager': {
                overallScore: 7.4,
                matches: [
                    'Sales Lead of 6-person team',
                    'Quota hit rate 92% (11 of 12 quarters)',
                    'Handled inventory receiving and opens/closes',
                ],
                concerns: [
                    'No documented P&L ownership yet',
                    'Hiring experience limited to training, not sourcing',
                ],
            },
            'retail-stock-associate': {
                overallScore: 7.0,
                matches: [
                    'Retail inventory-receiving experience',
                    'Comfortable with fast-paced floor work',
                ],
                concerns: [
                    'No warehouse-scanner or WMS experience',
                ],
            },
            'hospitality-server': {
                overallScore: 8.2,
                matches: [
                    '2 yr front-desk at Cascade Boutique Hotel',
                    'Strong customer-service and cash-handling',
                    'Guest-recovery mentioned in 40+ reviews',
                ],
                concerns: [
                    'No documented restaurant/serving experience specifically',
                ],
            },
            'hospitality-line-cook': {
                overallScore: 3.6,
                matches: [
                    'Strong work ethic and reliability',
                ],
                concerns: [
                    'No kitchen or food-prep experience',
                    'No food-safety certification',
                ],
            },
            'logistics-warehouse-associate': {
                overallScore: 5.5,
                matches: [
                    'Retail inventory-receiving experience',
                    'Reliable and trusted with shift-lead duties',
                ],
                concerns: [
                    'No warehouse-floor or forklift experience',
                    'No documented heavy-lift comfort',
                ],
            },
            'services-nail-technician': {
                overallScore: 4.8,
                matches: [
                    'Warm client-facing communication style',
                    'Trusted with cash and appointments',
                ],
                concerns: [
                    'No nail-technician license on record',
                    'No documented salon experience',
                ],
            },
        },
        defaultPreview: {
            overallScore: 6.8,
            matches: [
                '5+ years of customer-facing work across retail + hospitality',
                'Sales Lead with quota-hit rate of 92%',
                'Comfortable de-escalating upset customers',
                'Praised for guest-recovery in 40+ TripAdvisor reviews',
            ],
            concerns: [
                'Background is front-of-house — physical/technical fit for this role is limited',
            ],
        },
    },
    {
        id: 'sample-alex-kim',
        label: 'Alex Kim',
        icon: '🧑',
        tagline: 'Office admin → trade cert',
        data: {
            name: 'Alex Kim',
            location: 'Denver, CO',
            timezone: 'America/Denver',
            phone: '+1 720-555-0163',
            email: 'alex.kim@example.com',
            experience: [
                {
                    startDate: { year: 2021, month: 9 },
                    endDate: { year: 2025, month: 6 },
                    position: 'Office Administrator',
                    companyName: 'Mesa Property Group',
                    duties: 'Managed calendars, vendor invoices, and office supplies for a 25-person real-estate firm. Answered a busy phone line, greeted clients, organized quarterly team offsites.',
                },
            ],
            strengths: 'Organized, dependable, quick to learn new software. Recently completed a 12-week trade certificate program and is looking to switch careers to hands-on work.',
            weaknesses: 'No formal experience in warehouse, retail floor, or trade work — actively transitioning.',
            fiveYears: 'Build hands-on trade experience and grow into a skilled technician or team lead role.',
        },
        previewByJobTemplateId: {
            'general-administrative-assistant': {
                overallScore: 8.4,
                matches: [
                    '4 yr Office Administrator at Mesa Property Group',
                    'Managed calendars, invoices, and vendor coordination',
                    'Reliable phone-and-greeter experience',
                ],
                concerns: [
                    'Actively pivoting to trade work — confirm intent to stay in admin',
                ],
            },
            'logistics-warehouse-associate': {
                overallScore: 4.5,
                matches: [
                    'Reliable and organized',
                    'Comfortable with fast-paced task management',
                ],
                concerns: [
                    'No warehouse or physical-labor history on record',
                    'Trade certificate is recent — no field hours yet',
                ],
            },
            'retail-sales-associate': {
                overallScore: 5.5,
                matches: [
                    'Client-greeting experience from office role',
                    'Organized and dependable',
                ],
                concerns: [
                    'No retail-floor or POS experience',
                    'Career switcher — motivation is genuine but unproven',
                ],
            },
            'services-technician': {
                overallScore: 6.0,
                matches: [
                    'Recently completed 12-week trade certificate program',
                    'Motivated career switcher',
                    'Organized and detail-oriented',
                ],
                concerns: [
                    'No paid field hours yet',
                    'Would need shadow-training period',
                ],
            },
        },
        defaultPreview: {
            overallScore: 4.8,
            matches: [
                'Organized and dependable',
                'Recent trade-certificate graduate',
                'Motivated career switcher',
            ],
            concerns: [
                'Prior work is office admin — no direct experience in this role type',
                'Would need onboarding and shadow-training',
            ],
        },
    },
    {
        id: 'sample-chen-liu',
        label: 'Chen Liu',
        icon: '🧑‍🎓',
        tagline: 'New grad · 1 job',
        data: {
            name: 'Chen Liu',
            location: 'Columbus, OH',
            timezone: 'America/New_York',
            phone: '+1 614-555-0198',
            email: 'chen.liu@example.com',
            experience: [
                {
                    startDate: { year: 2024, month: 6 },
                    endDate: { year: 2024, month: 8 },
                    position: 'Summer Camp Counselor',
                    companyName: 'Camp Silverwood',
                    duties: 'Supervised groups of 10-12 kids ages 8-12. Ran outdoor activities and helped with meal service.',
                },
            ],
            strengths: 'High-school graduate, first entry-level job hunt. Reliable, coachable, eager to learn.',
            weaknesses: 'No work experience beyond one summer job. No certifications yet.',
            fiveYears: 'Get established in a full-time job, save for community college, and figure out a long-term path.',
        },
        previewByJobTemplateId: {
            'retail-cashier': {
                overallScore: 4.2,
                matches: [
                    'Comfortable with kids and quick interactions (camp counselor)',
                    'Reliable and coachable per prior manager',
                ],
                concerns: [
                    'No POS or cash-handling experience',
                    'Only one prior job on record',
                ],
            },
            'hospitality-server': {
                overallScore: 3.8,
                matches: [
                    'Helped with meal service at summer camp',
                    'Comfortable in fast-paced group settings',
                ],
                concerns: [
                    'No restaurant or serving experience',
                    'No food-safety certification',
                ],
            },
            'logistics-warehouse-associate': {
                overallScore: 3.2,
                matches: [
                    'Physically capable and coachable',
                ],
                concerns: [
                    'No warehouse or industrial experience',
                    'No forklift or scanner exposure',
                    'Only 2 months of prior work history',
                ],
            },
            'general-administrative-assistant': {
                overallScore: 3.4,
                matches: [
                    'Reliable per prior counselor role',
                ],
                concerns: [
                    'No office, software, or phone-handling experience',
                ],
            },
        },
        defaultPreview: {
            overallScore: 3.2,
            matches: [
                'Reliable and coachable per prior manager',
                'Physically active from camp-counselor role',
            ],
            concerns: [
                'Only one prior job on record (2 months)',
                'No relevant certifications or role-specific experience',
                'Would need full training from day one',
            ],
        },
    },
];
